import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useUpdateFreelancerOnboardingFormsProgressCurrentStep } from "../../hooks/useFreelancerOnboarding";
import { FreelancerOnboardingFormStep } from "../../models/enums/FreelancerOnboardingFormStep";
import { FreelancerProfile } from "../../models/FreelancerProfile";
import { zoullooColors } from "../../theme";

interface IncompleteCompanyInfoDialogProps {
	open: boolean;
	onClose: () => void;
	freelancerProfile: FreelancerProfile | undefined | null;
}

const IncompleteCompanyInfoDialog: React.FC<IncompleteCompanyInfoDialogProps> = ({
	open,
	onClose,
	freelancerProfile
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const updateCurrentStep = useUpdateFreelancerOnboardingFormsProgressCurrentStep({
		hideAllSnackbars: true
	});

	const handleNavigateToProfessionalInformation = async () => {
		// Persist the new onboarding step in DB BEFORE navigating. The back-end
		// picks the JSON Patch validation strategy from the persisted step, so
		// jumping straight to the form without updating the step first would
		// cause the next save on that form to be rejected as "incoherent paths".
		if (
			freelancerProfile?.onboarding?.id &&
			freelancerProfile?.onboarding?.formsProgress?.id
		) {
			try {
				await updateCurrentStep.mutateAsync({
					freelancerOnboardingId: freelancerProfile.onboarding.id,
					freelancerOnboardingFormsProgressId:
						freelancerProfile.onboarding.formsProgress.id,
					newStep: FreelancerOnboardingFormStep.MyProfessionalInformation
				});
				// Invalidate so the profile is refetched with the new current step
				// before the FreelancerOnboardingForms reads it from the cache.
				await queryClient.invalidateQueries({ queryKey: ["freelancerProfile"] });
			} catch (error) {
				console.error("Failed to switch onboarding step before navigation:", error);
				return;
			}
		}

		onClose();
		navigate("/profile");
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			aria-labelledby="incomplete-company-info-dialog-title"
			maxWidth="sm"
			fullWidth
		>
			<DialogTitle id="incomplete-company-info-dialog-title">
				{t("project.proposal.companyInfoIncomplete.title")}
			</DialogTitle>
			<DialogContent>
				<Typography variant="body1" sx={{ mb: 2 }}>
					{t("project.proposal.companyInfoIncomplete.description1")}
				</Typography>
				<Typography variant="body1" sx={{ mb: 3 }}>
					{t("project.proposal.companyInfoIncomplete.description2")}
				</Typography>

				<Box sx={{ mb: 3 }}>
					<Button
						variant="text"
						onClick={handleNavigateToProfessionalInformation}
						disabled={updateCurrentStep.isPending}
						startIcon={
							updateCurrentStep.isPending ? (
								<CircularProgress size={16} color="inherit" />
							) : undefined
						}
						sx={{
							color: zoullooColors.secondary.main,
							fontWeight: 500,
							textTransform: "none",
							p: 0,
							"&:hover": {
								backgroundColor: "transparent",
								textDecoration: "underline"
							}
						}}
					>
						{t("project.proposal.companyInfoIncomplete.link")}
					</Button>
				</Box>

				<Typography variant="body2" color="textSecondary">
					{t("project.proposal.companyInfoIncomplete.footer")}
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={onClose}
					color="inherit"
					disabled={updateCurrentStep.isPending}
				>
					{t("project.proposal.companyInfoIncomplete.close")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default IncompleteCompanyInfoDialog;
